import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'
import { locationSummaryQueryOptions } from '@/api/accommodation/hooks'
import { LocationOgCard } from '@/features/accommodation/og/LocationOgCard'
import { SHARE_FORMATS, type ShareFormat } from '@/features/accommodation/share/types'
import { Button } from '@/components/ui/button'
import { SITE_NAME, noindexMeta } from '@/lib/seo/siteMeta'

const shareFormatKeys = Object.keys(SHARE_FORMATS) as [ShareFormat, ...ShareFormat[]]

const cardSearchSchema = z.object({
  type: z.enum(shareFormatKeys).catch('og-image'),
})

const cardParamsSchema = z.object({
  slug: z.string().min(1),
})

export const Route = createFileRoute('/card/location/$slug')({
  component: CardPage,
  errorComponent: CardErrorComponent,
  validateSearch: (search) => cardSearchSchema.parse(search),
  params: {
    parse: (params) => cardParamsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    await context.queryClient.query({
      ...locationSummaryQueryOptions(params.slug),
      staleTime: 'static',
    })
  },
  head: () => ({
    meta: [{ title: `Sdílení místa | ${SITE_NAME}` }, ...noindexMeta()],
  }),
})

function CardPage() {
  const { slug } = Route.useParams()
  const { type } = Route.useSearch()
  const { data: location } = useSuspenseQuery(locationSummaryQueryOptions(slug))

  const format = SHARE_FORMATS[type]

  return (
    <LocationOgCard photos={location.photos} width={format.width} height={format.height} safeArea={format.safeArea} />
  )
}

function CardErrorComponent() {
  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">Chyba při načítání karty</h1>
      <p className="mb-6 max-w-sm text-slate-500">Místo se nepodařilo načíst. Zkontrolujte adresu.</p>
      <Button asChild>
        <Link to="/">Zpět na hlavní stránku</Link>
      </Button>
    </div>
  )
}
